import { ArrowLeft } from 'lucide-react'
import { DashboardCategoryTabs, type DashboardCategory } from './DashboardCategoryTabs'
import { INSIGHT_TILES, TILE_GROUPS } from '../dashboard/catalog'
import { dashboardCategoryPath } from '../lib/dashboardRoutes'
import { navigate, useRoute } from '../lib/router'

interface DashboardGroupSubMenuProps {
  /** Tile key of the page currently open, e.g. `four_wall`. */
  activeKey: string
  /** Group used when the page was opened directly rather than from a category. */
  fallbackGroupKey?: string
  /** The page panel the selected child tab controls. */
  panelId?: string
}

/**
 * The second-level menu shown inside a category's tile page.
 *
 * It reuses the exact category bar: every label visible, the orange selected pill and
 * the same pop animation. The first item is always Back; the remaining labels are every
 * tile in the category the page was opened from. Tiles without a screen of their own
 * stay visible but disabled.
 */
export function DashboardGroupSubMenu({
  activeKey,
  fallbackGroupKey = 'insights',
  panelId,
}: DashboardGroupSubMenuProps) {
  const route = useRoute()
  const fromKey = route.query.get('from')

  const fromGroup = TILE_GROUPS.find((candidate) => candidate.key === fromKey)
  const groupContainingActive = TILE_GROUPS.find((candidate) =>
    candidate.tiles.some((tile) => tile.key === activeKey),
  )
  const group =
    (fromGroup?.tiles.some((tile) => tile.key === activeKey) ? fromGroup : groupContainingActive) ??
    TILE_GROUPS.find((candidate) => candidate.key === fallbackGroupKey) ??
    INSIGHT_TILES

  const categories: DashboardCategory[] = [
    { key: 'back', label: 'Back', icon: ArrowLeft },
    ...group.tiles.map((tile) => ({ key: tile.key, label: tile.label })),
  ]
  const disabledKeys = group.tiles
    .filter((tile) => !tile.route && tile.key !== activeKey)
    .map((tile) => tile.key)

  const select = (key: string) => {
    if (key === 'back') {
      navigate(dashboardCategoryPath(group.key))
      return
    }
    if (key === activeKey) return

    const tile = group.tiles.find((candidate) => candidate.key === key)
    if (!tile?.route) return
    navigate(tile.route)
  }

  return (
    <DashboardCategoryTabs
      categories={categories}
      activeKey={activeKey}
      onSelect={select}
      disabledKeys={disabledKeys}
      panelId={panelId}
      ariaLabel={`${group.title} pages`}
    />
  )
}

export default DashboardGroupSubMenu
